import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, FormControlLabel,
  InputLabel, MenuItem, Paper, Select, Stack, TextField, Typography
} from '@mui/material';
import { AssignmentTurnedIn, Close, Send } from '@mui/icons-material';
import { disciplinesApi } from '../../services/api';
import authService from '../../services/auth/authService';
import NetworkSectionSelector from '../UserManagement/NetworkSectionSelector';

const SUB_DISCIPLINES = ['Pavement Defect', 'Traffic Safety'];

const AccessChangeRequestDialog = ({ open, onClose, user, onSubmitted }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [availableDisciplines, setAvailableDisciplines] = useState([]);
  const [resources, setResources] = useState({ networks: [] });
  const [disciplines, setDisciplines] = useState([]);
  const [subDisciplines, setSubDisciplines] = useState([]);
  const [locations, setLocations] = useState([]);
  const [permissionScope, setPermissionScope] = useState('network_section');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open || !user) return;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [disciplineResponse, resourceResponse, permissionResponse] = await Promise.all([
          disciplinesApi.getDisciplines(),
          authService.getAvailableResources(),
          authService.getUserPermissions(user.id)
        ]);
        const permissions = permissionResponse.permissions || {};
        const currentScope = user.permissionScope || user.permission_scope || 'network_section';
        setAvailableDisciplines(disciplineResponse.data || []);
        setResources(resourceResponse || { networks: [] });
        setDisciplines(permissions.disciplines || []);
        setSubDisciplines(permissions.sub_disciplines || []);
        setLocations(currentScope === 'network_section' ? (permissions.locations || []).map(item => item.network_id && !item.section_id
          ? { type: 'network', id: item.network_id, name: item.network_name }
          : { type: 'section', id: item.section_id, name: item.section_name, networkId: item.section_network_id }) : []);
        setPermissionScope(currentScope);
        setReason('');
      } catch (err) {
        setError(err.message || 'Failed to load your current access');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, user]);

  const roadSelected = disciplines.some(item => item.name === 'Road Pavement');
  const valid = disciplines.length > 0 && reason.trim().length >= 5 &&
    (!roadSelected || subDisciplines.length > 0) &&
    (permissionScope === 'organization' || locations.length > 0);

  const toggleDiscipline = discipline => {
    const selected = disciplines.some(item => item.id === discipline.id);
    setDisciplines(selected
      ? disciplines.filter(item => item.id !== discipline.id)
      : [...disciplines, { id: discipline.id, name: discipline.name }]);
    if (selected && discipline.name === 'Road Pavement') setSubDisciplines([]);
  };

  const toggleSubDiscipline = value => {
    setSubDisciplines(previous => previous.includes(value)
      ? previous.filter(item => item !== value)
      : [...previous, value]);
  };

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await authService.submitAccessChangeRequest({
        disciplines,
        subDisciplines,
        locations: permissionScope === 'network_section' ? locations : [],
        permissionScope,
        reason: reason.trim()
      });
      onSubmitted?.();
      onClose();
    } catch (err) {
      setError(err.error || 'Failed to submit access change request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center"><AssignmentTurnedIn color="primary" /><Typography variant="h6">Request Access Change</Typography></Stack>
          <Button color="inherit" size="small" onClick={onClose} disabled={submitting}><Close /></Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>Your current access remains active while a manager reviews this request. Select the complete access you need after reassignment.</Alert>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? <Stack alignItems="center" sx={{ py: 5 }}><CircularProgress /></Stack> : (
          <Stack spacing={2.5}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Disciplines</Typography>
              {availableDisciplines.map(discipline => (
                <Box key={discipline.id}>
                  <FormControlLabel control={<Checkbox checked={disciplines.some(item => item.id === discipline.id)} onChange={() => toggleDiscipline(discipline)} />} label={discipline.name} />
                  {discipline.name === 'Road Pavement' && roadSelected && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ pl: 4 }}>
                      {SUB_DISCIPLINES.map(item => <FormControlLabel key={item} control={<Checkbox size="small" checked={subDisciplines.includes(item)} onChange={() => toggleSubDiscipline(item)} />} label={item} />)}
                    </Stack>
                  )}
                </Box>
              ))}
            </Paper>

            <FormControl fullWidth size="small">
              <InputLabel>Requested Access Level</InputLabel>
              <Select value={permissionScope} label="Requested Access Level" onChange={event => setPermissionScope(event.target.value)}>
                <MenuItem value="network_section">Network/Section</MenuItem>
                <MenuItem value="organization">Organization-wide</MenuItem>
              </Select>
            </FormControl>

            {permissionScope === 'network_section' ? (
              <NetworkSectionSelector assignableResources={resources} selectedPermissions={locations} onChange={setLocations} error={null} />
            ) : <Alert severity="warning">Organization-wide access grants all networks and sections and still requires manager approval.</Alert>}

            <TextField fullWidth required label="Reason for reassignment" value={reason} onChange={event => setReason(event.target.value)} multiline rows={3} helperText="Briefly explain why your discipline or location access needs to change." />
          </Stack>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Send />} disabled={loading || submitting || !valid} onClick={submit}>{submitting ? 'Submitting...' : 'Submit Request'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccessChangeRequestDialog;
